/**
 * LeaveWizardDialog (#303)
 *
 * Design-system styled confirm shown when the user tries to leave the connector
 * creation wizard (sidebar nav, Back/Forward, breadcrumb) with unsaved changes.
 * The wizard has no "save" action mid-flow, so this is a focused two-button
 * Stay/Leave dialog (unlike the shared 3-button UnsavedChangesDialog used by the
 * edit flows). Built on the same AlertDialog primitives for visual consistency.
 */
import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export interface LeaveWizardDialogProps {
  /** Whether the dialog is open (blocker is in the 'blocked' state). */
  open: boolean;
  /** Stay on the wizard (cancel the navigation). */
  onStay: () => void;
  /** Leave without creating (proceed with the navigation). */
  onLeave: () => void;
}

export const LeaveWizardDialog: React.FC<LeaveWizardDialogProps> = ({
  open,
  onStay,
  onLeave,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onStay()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave without creating this connector?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. If you leave now, the connector will not be
            created and the configuration you entered will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onStay}>
            Stay
          </Button>
          <Button variant="destructive" onClick={onLeave}>
            Leave
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
